import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  type Notification,
  type NotificationType,
} from '@/db/notifications';
import { logger } from '@/utils/logger';

const NOTIFICATIONS_LIMIT = 20;

export function useNotifications() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterTypes, setFilterTypes] = useState<NotificationType[]>([]);
  const initialLoadDone = useRef(false);

  // Load notifications and unread count
  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const [items, count] = await Promise.all([
        getNotifications(userId, { limit: NOTIFICATIONS_LIMIT, types: filterTypes.length > 0 ? filterTypes : undefined }),
        getUnreadCount(userId),
      ]);
      setNotifications(items);
      setUnreadCount(count);
    } catch (err) {
      logger.warn('Failed to load notifications:', err);
    }
  }, [userId, filterTypes]);

  // Initial load
  useEffect(() => {
    if (!userId) return;
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      setLoading(true);
      refresh().finally(() => setLoading(false));
    } else {
      // Re-fetch when filters change
      refresh();
    }
  }, [userId, refresh]);

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          refresh();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refresh]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n,
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      logger.warn('Failed to mark notification as read:', err);
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!userId) return;
    try {
      await markAllAsRead(userId);
      setNotifications(prev => prev.map(n => ({ ...n, read: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      logger.warn('Failed to mark all notifications as read:', err);
    }
  }, [userId]);

  return {
    notifications,
    unreadCount,
    loading,
    refresh,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    filterTypes,
    setFilterTypes,
  };
}
